/**
 * District SSO sign-in (SSO_SPEC §6.1–6.2).
 *
 * Provider-agnostic: the district's configuration decides which Firebase auth
 * provider is used, so adding a district never means changing this code.
 *
 * SAML and OIDC providers require the project to be upgraded to Google Cloud
 * Identity Platform. Until that toggle is on, those sign-ins fail with
 * auth/operation-not-allowed — surfaced here as a clear message rather than a
 * raw Firebase error.
 */
import {
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  type Auth,
  type AuthProvider,
} from 'firebase/auth';
import { resolveSignInMethod, type SignInMethodResolution } from './cloudFunctions';

export type { SignInMethodResolution };

export class CleverNotConnectedError extends Error {
  constructor() {
    super('Clever sign-in is not connected for this district yet. Ask your district administrator, or sign in from your Clever portal.');
    this.name = 'CleverNotConnectedError';
  }
}

export class SsoNotEnabledError extends Error {
  constructor() {
    super(
      'Single sign-on is not enabled on this project yet. ' +
        'Upgrade to Google Cloud Identity Platform to use SAML/OIDC providers.'
    );
    this.name = 'SsoNotEnabledError';
  }
}

/**
 * Build the Firebase auth provider a district's configuration calls for.
 *
 * `provider_id` is authoritative — it is the id registered in the Identity
 * Platform console. `provider_type` only decides which SDK class wraps it.
 */
export function buildProvider(resolution: SignInMethodResolution): AuthProvider {
  const providerId = resolution.provider_id || '';

  switch (resolution.provider_type) {
    case 'google': {
      const provider = new GoogleAuthProvider();
      // `hd` restricts the account chooser to the district's domain. It is a
      // hint only — the real check happens server-side in beforeSignIn.
      const domain = providerId.startsWith('google.') ? providerId.slice(7) : undefined;
      if (domain) {
        provider.setCustomParameters({ hd: domain });
      }
      return provider;
    }

    case 'saml':
      return new SAMLAuthProvider(providerId);

    case 'oidc':
    case 'classlink':
      // ClassLink is OIDC-shaped once registered as a GCIP OIDC provider.
      return new OAuthProvider(providerId);

    case 'clever':
      // Clever is not a GCIP provider (CLEVER_SPEC §3): the browser goes to
      // Clever's authorize URL and returns to /auth/clever with a code that
      // exchangeCleverCode turns into a custom token. Handled in
      // signInWithDistrict; there is no Firebase provider to build.
      throw new CleverNotConnectedError();

    default:
      throw new Error(`Unsupported SSO provider type: ${resolution.provider_type}`);
  }
}

/**
 * Start the district sign-in.
 *
 * Redirect is the default for federated providers: popups are unreliable
 * inside ClassLink/Clever in-portal browsers and on managed Chromebooks, which
 * is exactly where district users are. Google keeps the popup because that
 * flow is already proven for existing users.
 */
export async function signInWithDistrict(
  auth: Auth,
  resolution: SignInMethodResolution
): Promise<void> {
  if (resolution.method !== 'sso' || !resolution.provider_id) {
    throw new Error('This account does not use district sign-in');
  }

  if (resolution.provider_type === 'clever') {
    if (!resolution.clever_authorize_url) throw new CleverNotConnectedError();
    // Full-page redirect: Clever's login runs in its own window and comes
    // back to /auth/clever. Popups break inside portal browsers.
    window.location.assign(resolution.clever_authorize_url);
    return;
  }

  const provider = buildProvider(resolution);

  try {
    if (resolution.provider_type === 'google') {
      await signInWithPopup(auth, provider);
      return;
    }
    await signInWithRedirect(auth, provider);
  } catch (error: any) {
    if (
      error?.code === 'auth/operation-not-allowed' ||
      error?.code === 'auth/invalid-provider-id'
    ) {
      throw new SsoNotEnabledError();
    }
    throw error;
  }
}

/**
 * Home-realm discovery for an email address.
 *
 * Unknown domains resolve to `password` — that is the normal answer for every
 * self-service user, not an error.
 */
export async function lookupSignInMethod(
  email: string
): Promise<SignInMethodResolution> {
  return resolveSignInMethod(email);
}
